import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, MailPlus, UserPlus, Users, X } from 'lucide-react';
import { useMe } from '@/api/auth';
import { useAnswerOffer, useMyTeamOffers, useSendOffer } from '@/api/teams';
import type { Guid, MeCommunityDto, TeamOfferStatus } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Dialog } from '@/components/ui/Dialog';
import { Field, Select, Textarea } from '@/components/ui/Field';
import { Section } from '@/components/ui/Page';
import { formatRelative } from '@/lib/format';
import { toastError } from '@/lib/errors';
import { RatingBadge } from './Feedback';
import { StatsLine } from './Market';

/** Freelance teams the signed-in person leads (they have the team panel and can send offers). */
export function useLedTeams(): MeCommunityDto[] {
  const { data: me } = useMe();
  return (me?.communities ?? []).filter((c) => c.role === 'Lead' && c.status === 'Active' && c.isPublic);
}

export function OfferStatusBadge({ status }: { status: TeamOfferStatus }) {
  const { t } = useTranslation();
  const tone = status === 'Accepted' ? 'trusted' : status === 'Pending' ? 'check' : 'muted';
  return <Badge tone={tone}>{t(`offers.status.${status}`)}</Badge>;
}

/** The lead writes a few words and sends the offer (choosing the team when they lead more than one). */
export function SendOfferDialog({
  open, onOpenChange, tester, teamId, onSent,
}: { open: boolean; onOpenChange: (o: boolean) => void; tester: { userId: Guid; displayName: string } | null; teamId?: Guid; onSent?: () => void }) {
  const { t } = useTranslation();
  const teams = useLedTeams();
  const send = useSendOffer();
  const [chosen, setChosen] = useState<Guid | ''>('');
  const [message, setMessage] = useState('');
  const team = teamId ?? (chosen || teams[0]?.id);
  const submit = () => {
    if (!tester || !team) return;
    send.mutate({ teamId: team, userId: tester.userId, message: message.trim() || null }, {
      onSuccess: () => {
        toast.success(t('offers.sent', { name: tester.displayName }));
        setMessage('');
        onOpenChange(false);
        onSent?.();
      },
      onError: (e) => toastError(e),
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('offers.dialogTitle', { name: tester?.displayName ?? '' })} description={t('offers.dialogHint')}
      footer={(
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" icon={<MailPlus className="size-4" />} loading={send.isPending} disabled={!team} onClick={submit}>{t('offers.send')}</Button>
        </>
      )}>
      <div className="flex flex-col gap-3" data-testid="offer-dialog">
        {!teamId && teams.length > 1 && (
          <Field label={t('offers.team')}>
            {(p) => (
              <Select {...p} value={team} onChange={(e) => setChosen(e.target.value)}>
                {teams.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </Select>
            )}
          </Field>
        )}
        <Field label={t('offers.message')} hint={t('offers.messageHint')}>
          {(p) => <Textarea {...p} rows={4} maxLength={1000} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t('offers.messagePlaceholder')} />}
        </Field>
      </div>
    </Dialog>
  );
}

/** On a freelancer's page: team leads invite them. */
export function OfferButton({ userId, displayName }: { userId: Guid; displayName: string }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const teams = useLedTeams();
  const [open, setOpen] = useState(false);
  if (teams.length === 0 || me?.id === userId) return null;
  return (
    <>
      <Button variant="secondary" size="sm" icon={<UserPlus className="size-4" />} onClick={() => setOpen(true)}>{t('offers.invite')}</Button>
      <SendOfferDialog open={open} onOpenChange={setOpen} tester={{ userId, displayName }} />
    </>
  );
}

/** A tester's open offers, with accept / decline. */
export function MyTeamOffers() {
  const { t, i18n } = useTranslation();
  const offers = useMyTeamOffers();
  const answer = useAnswerOffer();
  const list = offers.data ?? [];
  if (list.length === 0) return null;
  return (
    <Section title={t('offers.inboxTitle', { count: list.length })} description={t('offers.inboxHint')} className="mb-5">
      <ul className="divide-y divide-line" data-testid="team-offers">
        {list.map((o) => (
          <li key={o.id} className="flex flex-col gap-2 px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Users className="size-4 text-accent" />
              <Link to={`/teams/${o.team.id}`} className="font-semibold hover:text-accent-text hover:underline">{o.team.name}</Link>
              <span className="text-xs text-ink-3">{t('offers.from', { name: o.invitedByName, at: formatRelative(o.createdAt, i18n.language) })}</span>
              <span className="ml-auto flex flex-wrap items-center gap-x-3"><StatsLine stats={o.team.stats} /><RatingBadge rating={o.team.rating} /></span>
            </div>
            <p className="text-xs text-ink-2">{t('market.teamOf', { count: o.team.memberCount })}{o.team.skills.length > 0 && ` · ${o.team.skills.slice(0, 5).join(', ')}`}</p>
            {o.message && <p className="whitespace-pre-wrap rounded-[3px] border-l-2 border-accent bg-surface-2 px-2.5 py-1.5 text-sm">{o.message}</p>}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="primary" icon={<Check className="size-3.5" />} loading={answer.isPending && answer.variables?.offerId === o.id && answer.variables.accept}
                onClick={() => answer.mutate({ offerId: o.id, accept: true }, {
                  onSuccess: () => toast.success(t('offers.accepted', { team: o.team.name })), onError: (e) => toastError(e),
                })}>
                {t('offers.accept')}
              </Button>
              <Button size="sm" variant="secondary" icon={<X className="size-3.5" />} loading={answer.isPending && answer.variables?.offerId === o.id && !answer.variables.accept}
                onClick={() => answer.mutate({ offerId: o.id, accept: false }, {
                  onSuccess: () => toast.success(t('offers.declined', { team: o.team.name })), onError: (e) => toastError(e),
                })}>
                {t('offers.decline')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}
